import React from 'react';
import PropTypes from 'prop-types';
import PatientUtils from 'services/PatientUtils';
import CalendarUtils from 'services/CalendarUtils';
import Icon from 'components/Utils/NewIcon';
import UnreadBadge from 'components/Common/UnreadBadge';
import './style.scss';
import { ProfileImage } from 'components/Common/UserImage';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Portal } from '@material-ui/core';
import AppointmentUtils from 'services/AppointmentUtils';

const ChatBubble = ({
  user,
  photo,
  name,
  onClick,
  selected,
  unreadCount,
  patientInRoom,
  defaultPhoto,
  loading,
  icons = [],
  className,
  badgeColor,
  appointment,
  bubbleId,
  showTooltip = false,
  extraTooltip,
  notes,
}) => {
  const patientName = user ? PatientUtils.getName(user) : name;
  const tooltipContent = showTooltip && patientName && (
    <BubbleTooltip
      patientName={patientName}
      appointment={appointment}
      notes={notes}
      extraTooltip={extraTooltip}
    />
  );

  return (
    <>
      <div
        className={getClassNames(selected, patientInRoom, appointment, className)}
        onClick={onClick}
      >
        <UnreadBadge
          key={unreadCount}
          unreadCount={unreadCount}
          className="animated heartBeat slow"
          style={{ backgroundColor: badgeColor }}
        />
        {icons.map((icon) => (
          <Icon src={icon} key={icon} width="20px" height="20px" fontAwesomeStyle="fas" />
        ))}
        {!!user && (
          <>
            {!!user?.photo && <ProfileImage userRole="patient" user={user} size={35} />}
            {!user?.photo && <Initials userRole="patient" user={user} />}
          </>
        )}
        {loading && <ProgressMessage size={39} />}
        {!user && <ProfileImage size={35} photo={photo} defaultPhoto={defaultPhoto} />}
        <span className="name truncate px-1">{name || PatientUtils.getShortName(user)}</span>
        <SelectedMarker selected={selected} />
      </div>

      {tooltipContent && bubbleId && (
        <Portal>
          <ReactTooltip
            anchorId={bubbleId}
            place="bottom"
            className="careand-tooltip"
            delayShow={50}
          >
            {tooltipContent}
          </ReactTooltip>
        </Portal>
      )}
    </>
  );
};

export const Initials = (props) => (
  <div className="initials center-cropped avatar">
    <span>{PatientUtils.getInitials(props.user)}</span>
  </div>
);

const SelectedMarker = ({ selected }) => selected && <div className="marker" />;

function getClassNames(selected, patientInRoom, appointment, additionalClassNames) {
  let classNames = 'chat-bubble animated bounceIn';
  if (selected) classNames += ' selected';
  if (patientInRoom) classNames += ' patient-in-room';
  if (appointment) classNames += ` status-${appointment.status}`;
  if (additionalClassNames) classNames += ` ${additionalClassNames}`;
  return classNames;
}

const BubbleTooltip = ({ patientName, notes, appointment, extraTooltip }) => {
  const appointmentTime =
    appointment?.date && appointment?.date_end
      ? `${CalendarUtils.format(appointment.date, 'Do MMM [at] HH:mm A')} - ${CalendarUtils.format(
          appointment.date_end,
          'HH:mm A',
        )}`
      : '';
  return (
    <div className="bubble-notes-tooltip">
      <div className="tooltip-header">
        <strong>{patientName}</strong>
        {appointmentTime && <div className="appointment-time">{appointmentTime}</div>}
        {appointment && (
          <div className="appointment-time">
            {AppointmentUtils.getAppointmentDescription(appointment)} - {appointment?.status}
          </div>
        )}
        {extraTooltip && <div className="appointment-time">{extraTooltip}</div>}
      </div>
      {notes?.trim() && (
        <>
          <hr />
          <div className="tooltip-notes">
            <pre>{notes}</pre>
          </div>
        </>
      )}
      <div className="tooltip-footer">Right click to edit notes</div>
    </div>
  );
};

ChatBubble.propTypes = {
  user: PropTypes.object,
  photo: PropTypes.string,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  onContextMenu: PropTypes.func,
  unreadCount: PropTypes.number,
  bubbleId: PropTypes.string,
  showTooltip: PropTypes.bool,
  appointment: PropTypes.object,
};

export default ChatBubble;
